// The details behind a way to pay less, opened from its card: a pharmacy
// savings program, or a clinic's discount or financing option. Both read the
// same way: what it is, who qualifies, what it covers, and where it came from.
import { Linking, ScrollView, View } from "react-native";
import { ExternalLink, type LucideIcon } from "lucide-react-native";
import {
  Dialog,
  DialogBodyText,
  DialogHeader,
  MetaText,
  MoonDetailRow,
  Tag,
  TextActionButton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { EMBER } from "../../../onboarding/theme";

export type ProgramDetailsFact = {
  label: string;
  value: string;
  icon: LucideIcon;
};

type Props = {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  /** What the program saves, shown as a green tag under the title. */
  savingsLabel?: string;
  summary?: string;
  facts: ProgramDetailsFact[];
  /** The page the details came from, and the day it was last checked. */
  source?: { url: string; checkedOn: string };
  onClose: () => void;
  /** Analytics actions for the close button, the backdrop and the source link. */
  analytics: { close: string; backdrop: string; openSource: string };
  testID?: string;
};

export function ProgramDetailsDialog({
  title,
  subtitle,
  icon: Icon,
  savingsLabel,
  summary,
  facts,
  source,
  onClose,
  analytics,
  testID,
}: Props) {
  const t = useTranslations();
  const { long: formatLongDate } = useDateFormat();

  return (
    <Dialog
      size="wide"
      visible
      onClose={onClose}
      closeButton="minimal"
      closeAccessibilityLabel={t("access.program_details.close")}
      closeAction={analytics.close}
      backdropPressLabel={analytics.backdrop}
      testID={testID}
    >
      {/* The title stays beside the close; what follows scrolls below it. */}
      <DialogHeader
        leading={
          Icon ? (
            <Icon size={22} color={EMBER.colors.textMid} strokeWidth={1.6} />
          ) : null
        }
        title={title}
        subtitle={subtitle}
        style={{ marginBottom: 16 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flexShrink: 1 }}
        contentContainerStyle={{ gap: 16 }}
      >
        {savingsLabel ? (
          <Tag
            label={savingsLabel}
            tone="green"
            style={{ alignSelf: "flex-start" }}
          />
        ) : null}
        {summary ? <DialogBodyText>{summary}</DialogBodyText> : null}
        {facts.map((fact) => (
          <MoonDetailRow
            key={fact.label}
            icon={fact.icon}
            label={fact.label}
            value={
              <DialogBodyText style={{ marginTop: 0 }}>
                {fact.value}
              </DialogBodyText>
            }
          />
        ))}
        {source ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <MetaText style={{ flexShrink: 1 }}>
              {t("access.program_details.source_checked", {
                params: { date: formatLongDate(source.checkedOn) },
              })}
            </MetaText>
            <TextActionButton
              label={t("access.program_details.view_source")}
              accessibilityLabel={`${t("access.program_details.view_source")} ${title}`}
              action={analytics.openSource}
              onPress={() => void Linking.openURL(source.url)}
              icon={ExternalLink}
              iconPosition="trailing"
              size="sm"
            />
          </View>
        ) : null}
      </ScrollView>
    </Dialog>
  );
}
